import { IntegrationSetupStepType, BaseAuthWithSecrets, SecretFieldType } from "../../../config/types";
import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";
import MainConnectorSetupStepBase, { Inputs } from "./MainConnectorSetupStepBase";
import useIntegrationSetupContext from "./useIntegrationSetupContext";

const Type = IntegrationSetupStepType.STEP_TYPE_SECRETS as const;

type Props = {
  secretsAuth: BaseAuthWithSecrets;
  onSubmit?: (navigateToNextStep: () => void) => void;
  errorMessage?: string;
};

// Field names that should be masked as password inputs
const REDACTED_FIELD_PATTERNS = ['password', 'passphrase', 'secret', 'token', 'key', 'credential'];

const shouldRedactField = (fieldName: string): boolean => {
  const lowerName = fieldName.toLowerCase();
  return REDACTED_FIELD_PATTERNS.some(pattern => lowerName.includes(pattern));
};

const ConnectorSetupStepSecrets = ({
  secretsAuth,
  onSubmit,
  errorMessage,
}: Props) => {
  const { getFormDataProperty, setFormDataProperties } =
    useIntegrationSetupContext();
  const { translate } = useAgentHandlerLinkModalContext();

  const inputs: Inputs = secretsAuth.registered_user_secrets.map(secret => {
    // For select fields, use default option if no value is set
    let currentValue = getFormDataProperty(secret.name) ?? "";
    if (secret.field_type === SecretFieldType.SELECT && !currentValue && secret.options) {
      const defaultOption = secret.options.find(opt => opt.default);
      if (defaultOption) {
        currentValue = defaultOption.value;
        setFormDataProperties(secret.name, defaultOption.value);
      }
    }

    return {
      placeholder: secret.human_readable_name ?? secret.name,
      value: currentValue,
      setValue: (value: string) => setFormDataProperties(secret.name, value),
      labelSubtitle: secret.description,
      prefix: secret.prefix,
      suffix: secret.suffix,
      isRedacted: shouldRedactField(secret.name),
      fieldType: secret.field_type ?? SecretFieldType.TEXT,
      selectOptions: secret.options,
      optional: secret.optional === true,
    };
  });

  return (
    <MainConnectorSetupStepBase
      inputs={inputs}
      header={
        translate("enter-account-details", `Enter your account details`)
      }
      subHeader={
        null
      }
      description={secretsAuth.description ?? ""}
      isStepComplete={inputs.every(input => input.optional || !!input.value)}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
    />
  );
};

ConnectorSetupStepSecrets.Type = Type;

export default ConnectorSetupStepSecrets;
